import ActiveIndicator from "@app/components/active-indicator.tsx";
import DeleteButton from "@app/components/delete-button.tsx";
import { BudgetEntriesModel } from "@app/data/engine.ts";
import { formatUsd } from "@app/helpers/money-helper";
import { commaSeperate } from "@app/helpers/number-helper";
import EditIcon from "@mui/icons-material/Edit";
import { Box } from "@mui/material";
import { createColumnHelper, IconButton } from "bi-lib";

interface Props {
    catId: number;
    onEdit: (id: number) => void;
    onDelete: (id: number) => void;
    rateType: "billing" | "cost" | "internal";
    quantityTotal: number;
    billingAmountTotal: number;
    costAmountTotal: number;
    internalAmountTotal: number;
    rowCount: number;
}

const columnHelper = createColumnHelper<BudgetEntriesModel>();
export const assignmentBudgetTableDefinition = ({
    catId,
    onEdit,
    onDelete,
    rateType,
    quantityTotal,
    billingAmountTotal,
    costAmountTotal,
    internalAmountTotal,
    rowCount,
}: Props) => [
    columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (item) => (
            <Box display={"flex"}>
                <IconButton tooltip={"Edit"} onClick={() => onEdit(item.row.original.assignmentBudgetEntryId!)}>
                    <EditIcon />
                </IconButton>
                <DeleteButton onDelete={() => onDelete(item.row.original.assignmentBudgetEntryId!)} />
            </Box>
        ),
        footer: () => `${rowCount} Rows`,
    }),
    columnHelper.accessor((x) => x.staffingName, {
        id: "staffingName",
        header: "Staff",
    }),
    ...(catId == 1
        ? [
              columnHelper.accessor((x) => x.assignmentPhaseCode, {
                  id: "assignmentPhaseCode",
                  header: "Phase",
              }),
              columnHelper.accessor((x) => x.workPackageCode, {
                  id: "workPackageCode",
                  header: "WP",
              }),
          ]
        : []),
    columnHelper.accessor((x) => x.subcategoryGroup, {
        id: "subcategoryGroup",
        header: "Expense Group",
    }),
    columnHelper.accessor((x) => x.roleCode, {
        id: "roleCode",
        header: "Role",
    }),
    columnHelper.accessor((x) => x.quantity, {
        id: "quantity",
        header: catId == 1 ? "Hours" : "Amount",
        cell: (item) => (catId == 1 ? commaSeperate(item.getValue()) : formatUsd(item.getValue())),
        footer: () => (catId == 1 ? commaSeperate(quantityTotal) : formatUsd(quantityTotal)),
        alignment: "right",
    }),
    columnHelper.accessor((x) => x.billable, {
        id: "billable",
        header: "Billable",
        cell: (item) => <ActiveIndicator isActive={item.getValue()} />,
    }),
    columnHelper.accessor((x) => x.invoiceVisible, {
        id: "invoiceVisible",
        header: "Visible",
        cell: (item) => <ActiveIndicator isActive={item.getValue()} />,
    }),
    ...(rateType == "billing"
        ? [
              columnHelper.accessor((x) => x.billingRate, {
                  id: "billingRate",
                  header: "Bill Rate",
                  cell: (item) => formatUsd(item.getValue()),
                  alignment: "right",
              }),
              columnHelper.accessor((x) => x.billingAmount, {
                  id: "billingAmount",
                  header: "Bill Amount",
                  cell: (item) => formatUsd(item.getValue()),
                  footer: formatUsd(billingAmountTotal),
                  alignment: "right",
              }),
          ]
        : rateType == "cost"
          ? [
                ...(catId == 1 // Only include costRate when catId is 1
                    ? [
                          columnHelper.accessor((x) => x.costRate, {
                              id: "costRate",
                              header: "Cost Rate",
                              cell: (item) => formatUsd(item.getValue()),
                              alignment: "right",
                          }),
                      ]
                    : []),
                columnHelper.accessor((x) => x.costAmount, {
                    id: "costAmount",
                    header: "Cost Amount",
                    cell: (item) => formatUsd(item.getValue()),
                    footer: formatUsd(costAmountTotal),
                    alignment: "right",
                }),
            ]
          : [
                columnHelper.accessor((x) => x.internalRate, {
                    id: "internalRate",
                    header: "Internal Rate",
                    cell: (item) => formatUsd(item.getValue()),
                    alignment: "right",
                }),
                columnHelper.accessor((x) => x.internalAmount, {
                    id: "internalAmount",
                    header: "Internal Amount",
                    cell: (item) => formatUsd(item.getValue()),
                    footer: formatUsd(internalAmountTotal),
                    alignment: "right",
                }),
            ]),
];
